import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  GridAutosizeOptions,
  GridColDef,
  GridColumnHeaderParams,
  GridValidRowModel
} from "@mui/x-data-grid-pro";
import { StyledDataGrid } from "./ReportTable.styles";
import { ReportTableProps } from "./ReportTable.props";

export function ReportTable<Data extends GridValidRowModel>({
  data,
  columns
}: ReportTableProps<Data>) {
  const { t } = useTranslation();

  const renderHeader = useCallback(
    ({ colDef: { headerName } }: GridColumnHeaderParams) => t(headerName ?? ""),
    [t]
  );

  const columnDefinitions: GridColDef[] = useMemo(
    () =>
      columns.map(({ name, translationKey, valueFormatter, minWidth }) => ({
        field: name,
        headerName: translationKey,
        renderHeader,
        valueFormatter,
        minWidth
      })),
    [columns, renderHeader]
  );

  const autoSizeOptions: GridAutosizeOptions = useMemo(
    () => ({
      columns: columns.map(({ name }) => name),
      includeOutliers: true,
      disableColumnVirtualization: true,
      expand: true
    }),
    [columns]
  );

  return (
    <StyledDataGrid
      rows={data}
      columns={columnDefinitions}
      disableColumnMenu
      disableColumnReorder
      disableColumnSorting
      disableColumnResize
      disableVirtualization
      autosizeOnMount
      rowHeight={28}
      columnHeaderHeight={36}
      autosizeOptions={autoSizeOptions}
      rowSelection={false}
      hideFooterRowCount
    />
  );
}
